// Task table columns: which can be hidden, which sort on what, and the
// person's own choice of hidden columns (kept in this browser only — Volkan,
// 2026-10-22: "herkes kendine").
import { useState } from 'react'
import type { TaskSort } from './api'

export type TaskColumn =
  | 'assignee'
  | 'due'
  | 'status'
  | 'project'
  | 'stage'
  | 'priority'
  | 'subtasks'
  | 'created_by'
  | 'created_at'

export const TASK_COLUMNS: { key: TaskColumn; sort?: TaskSort }[] = [
  { key: 'assignee', sort: 'assignee' },
  { key: 'due', sort: 'due_date' },
  { key: 'status', sort: 'status' },
  { key: 'project', sort: 'project' },
  { key: 'stage', sort: 'stage' },
  { key: 'priority', sort: 'priority' },
  { key: 'subtasks' },
  { key: 'created_by', sort: 'created_by' },
  { key: 'created_at', sort: 'created_at' },
]

const DEFAULT_HIDDEN: TaskColumn[] = ['stage', 'created_by', 'created_at']
const STORAGE_KEY = 'pexify.staffTasks.hiddenColumns.v1'

function readHidden(): TaskColumn[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    const parsed: unknown = raw ? JSON.parse(raw) : null
    if (Array.isArray(parsed)) {
      const known = new Set<string>(TASK_COLUMNS.map((c) => c.key))
      return parsed.filter((key): key is TaskColumn => typeof key === 'string' && known.has(key))
    }
  } catch {
    // private window / blocked storage: fall back to the defaults
  }
  return DEFAULT_HIDDEN
}

/** The hidden columns and a setter that also remembers the choice. */
export function useHiddenColumns(): [TaskColumn[], (column: TaskColumn, shown: boolean) => void] {
  const [hidden, setHidden] = useState<TaskColumn[]>(readHidden)
  const toggle = (column: TaskColumn, shown: boolean) => {
    setHidden((current) => {
      const next = shown ? current.filter((c) => c !== column) : [...new Set([...current, column])]
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
      } catch {
        // not remembered; the choice still holds for this visit
      }
      return next
    })
  }
  return [hidden, toggle]
}

export type SortState = { key: TaskSort; dir: 'asc' | 'desc' } | null

/** Header click: ascending → descending → back to the usual order. */
export function nextSort(current: SortState, key: TaskSort): SortState {
  if (!current || current.key !== key) return { key, dir: 'asc' }
  return current.dir === 'asc' ? { key, dir: 'desc' } : null
}
